import React, { useEffect, useState } from "react";
import logger from "../../logger";
import {
  Button,
  Text,
  MessageBar,
  MessageBarBody,
  Dropdown,
  Option,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowSyncRegular,
  AddRegular,
  TableAddRegular,
  DeleteRegular,
} from "@fluentui/react-icons";
import { fetchAllCompanies, fetchSelectedCompany } from "./companyService";
import { useCompanyManagementStyles } from "./CompanyManagement.styles";

const CompanyManagement = () => {
  const [excelData, setExcelData] = useState("");
  const [excelError, setExcelError] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [dropdownKey, setDropdownKey] = useState(0);
  const [companyDataRange, setCompanyDataRange] = useState(null);
  const styles = useCompanyManagementStyles();

  const companyNameToIdMap = React.useMemo(() => {
    const map = {};
    companies.forEach((company) => {
      const companyName = company.name || company.id;
      map[companyName] = company.id;
    });
    return map;
  }, [companies]);

  const selectedCompanyDisplayName = React.useMemo(() => {
    if (!selectedCompanyId) return undefined;
    const company = companies.find((c) => c.id === selectedCompanyId);
    return company ? company.name || company.id : undefined;
  }, [selectedCompanyId, companies]);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setCompaniesLoading(true);
      const companiesData = await fetchAllCompanies();
      setCompanies(companiesData);
    } catch (error) {
      logger.error("Error loading companies:", error);
      setExcelData("Error loading companies: " + (error.message || "Unknown error"));
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const insertCompanyDataToExcel = async (companiesData) => {
    try {
      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();

        if (!companiesData || companiesData.length === 0) {
          throw new Error("No company data to insert");
        }

        const selectedRange = context.workbook.getSelectedRange();
        selectedRange.load("rowIndex, columnIndex");
        await context.sync();

        const startRow = selectedRange.rowIndex + 1;
        const startColIndex = selectedRange.columnIndex;

        const headers = ["Company Id", "Company Name", "Fund Id", "Fund Name", "Custom Id"];

        const result = [headers];

        for (let i = 0; i < companiesData.length; i++) {
          const company = companiesData[i];
          if (!company) continue;

          const companyId = company.id || "";
          const companyName = company.name || "";
          const customId = company.companyId || "";

          // Check if company has funds
          if (company.funds && Array.isArray(company.funds) && company.funds.length > 0) {
            // Create a row for each fund
            company.funds.forEach((fund) => {
              const fundId = fund.id || "";
              const fundName = fund.name || "";
              const row = [companyId, companyName, fundId, fundName, customId];
              result.push(row);
            });
          } else {
            // If no funds, create a single row with empty fund columns
            const row = [companyId, companyName, "", "", customId];
            result.push(row);
          }
        }

        const numRows = result.length;
        const numCols = headers.length;

        const getColumnLetter = (colNum) => {
          let result = "";
          let num = colNum;
          while (num > 0) {
            num--;
            result = String.fromCharCode(65 + (num % 26)) + result;
            num = Math.floor(num / 26);
          }
          return result;
        };

        const startCol = getColumnLetter(startColIndex + 1);
        const endCol = getColumnLetter(startColIndex + numCols);
        const endRow = startRow + numRows - 1;

        const dataRange = sheet.getRange(`${startCol}${startRow}:${endCol}${endRow}`);
        dataRange.values = result;

        const headerRange = sheet.getRange(`${startCol}${startRow}:${endCol}${startRow}`);
        headerRange.format.fill.color = "#1aebff";
        headerRange.format.font.color = "black";
        headerRange.format.font.bold = true;

        dataRange.format.autofitColumns();

        await context.sync();

        setCompanyDataRange({
          startCol,
          endCol,
          startRow,
          endRow,
        });

        setExcelData("Company data successfully inserted into Excel!");
        setExcelError(false);

        setTimeout(() => {
          setExcelData("");
        }, 3000);
      });
    } catch (error) {
      logger.error("Error inserting company data to Excel:", error);
      setExcelData("Error inserting company data to Excel: " + (error.message || "Unknown error"));
      setExcelError(true);
    }
  };

  const clearCompanyDataRange = async () => {
    try {
      if (!companyDataRange) {
        setExcelData("No company data range found to clear.");
        setExcelError(true);
        setTimeout(() => {
          setExcelData("");
        }, 3000);
        return;
      }

      await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();

        const { startCol, endCol, startRow, endRow } = companyDataRange;
        const fullRange = sheet.getRange(`${startCol}${startRow}:${endCol}${endRow}`);

        fullRange.clear(Excel.ClearApplyTo.all);

        await context.sync();
      });
    } catch (error) {
      logger.error("Error clearing company data range:", error);
      throw error;
    }
  };

  const handleClearAll = async () => {
    setSelectedCompanyId("");
    setDropdownKey((prev) => prev + 1);

    try {
      await clearCompanyDataRange();
      setCompanyDataRange(null);
      setExcelData("Company data cleared successfully!");
      setExcelError(false);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    } catch (error) {
      logger.error("Error clearing company data:", error);
      setExcelData("Error clearing company data: " + (error.message || "Unknown error"));
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    }
  };

  const handleSelectedCompanyGet = async () => {
    if (!selectedCompanyId) {
      setExcelData("Please select a company first");
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
      return;
    }

    try {
      setCompaniesLoading(true);
      const companyData = await fetchSelectedCompany(selectedCompanyId);
      if (companyData && typeof companyData === "object" && !Array.isArray(companyData)) {
        await insertCompanyDataToExcel([companyData]);
      } else {
        setExcelData("No company data found");
        setExcelError(true);
        setTimeout(() => {
          setExcelData("");
        }, 3000);
      }
    } catch (error) {
      setExcelData("Error fetching selected company: " + (error.message || "Unknown error"));
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleInsertAllCompanies = async () => {
    if (companies.length === 0) {
      setExcelData("No companies available. Please fetch companies first.");
      setExcelError(true);
      setTimeout(() => {
        setExcelData("");
      }, 3000);
      return;
    }

    await insertCompanyDataToExcel(companies);
  };

  return (
    <div className={styles.container}>
      <div className={styles.dropdownContainer}>
        <Text as="p" className={styles.label}>
          Select a Company:
        </Text>
        <Dropdown
          key={dropdownKey}
          placeholder={companiesLoading ? "Loading companies..." : "Select a company"}
          value={selectedCompanyDisplayName || ""}
          disabled={companiesLoading}
          onOptionSelect={(event, data) => {
            const selectedCompanyName = data.optionValue || "";
            const companyId = companyNameToIdMap[selectedCompanyName] || "";
            setSelectedCompanyId(companyId);
          }}
          className={styles.dropdown}
        >
          {companies.map((company) => {
            const companyName = company.name || company.id;
            return (
              <Option key={company.id} value={companyName}>
                {companyName}
              </Option>
            );
          })}
        </Dropdown>
      </div>

      <div className={styles.buttonsContainer}>
        <Tooltip
          withArrow
          content={{ children: "Refetch Data", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="secondary"
            onClick={loadCompanies}
            disabled={companiesLoading}
            className={styles.refetchButton}
            icon={<ArrowSyncRegular />}
          />
        </Tooltip>
        <Tooltip
          withArrow
          content={{ children: "Insert Company", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="primary"
            onClick={handleSelectedCompanyGet}
            disabled={companiesLoading || !selectedCompanyId}
            className={styles.insertButton}
            icon={<AddRegular />}
          />
        </Tooltip>
        {companies.length > 0 && (
          <Tooltip
            withArrow
            content={{ children: "Insert All Company", className: styles.tooltip }}
            relationship="label"
            positioning="below"
          >
            <Button
              appearance="primary"
              onClick={handleInsertAllCompanies}
              disabled={companiesLoading}
              className={styles.insertAllButton}
              icon={<TableAddRegular />}
            />
          </Tooltip>
        )}
        <Tooltip
          withArrow
          content={{ children: "Clear Company Data", className: styles.tooltip }}
          relationship="label"
          positioning="below"
        >
          <Button
            appearance="secondary"
            onClick={handleClearAll}
            disabled={companiesLoading}
            className={styles.clearButton}
            icon={<DeleteRegular />}
          />
        </Tooltip>
      </div>

      {excelData && (
        <MessageBar
          intent={excelError ? "error" : "success"}
          className={excelError ? styles.errorMessage : styles.successMessage}
        >
          <MessageBarBody>{excelData}</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
};

export default CompanyManagement;
